import { useEffect, useRef, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  ClipboardPaste,
  Loader2,
  RefreshCw,
  Send,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/common/page-header";
import { ROUTES } from "@/config/routes";
import { usePaymentsList } from "@/hooks/usePaymentQueries";
import {
  useSimulateWebhook,
  useTPaygateConfig,
} from "@/hooks/useTPaygateQueries";
import {
  formatAmountInput,
  parseAmountInput,
  toAmountInput,
} from "@/lib/amountInput";
import { formatCurrency } from "@/lib/currency";
import { formatVietnamDateTime } from "@/lib/date-utils";
import {
  webhookSimulationSchema,
  type WebhookSimulationForm,
} from "@/schemas/tpaygate.schema";
import type { PaymentListDto } from "@/services/admin-payment.service";
import type {
  TPaygateConfigDto,
  TPaygateWebhookOutcome,
  WebhookSimulationResult,
} from "@/types/tpaygate";

/** The order type a wallet top-up is recorded under. Its bill funds the wallet, not a purchase. */
const TOP_UP_ORDER_TYPE = "BalanceTopUp";

/** A bill with no order behind it is a top-up opened before top-ups had orders. */
function isTopUp(orderType?: string | null): boolean {
  return !orderType || orderType === TOP_UP_ORDER_TYPE;
}

/** What a bill pays for, in the operator's words. */
function orderTypeLabel(orderType?: string | null): string {
  if (!orderType || isTopUp(orderType)) {
    return "Top-up";
  }

  return orderType;
}

/** What each settlement decision means for the operator reading the result. */
const OUTCOME_COPY: Record<TPaygateWebhookOutcome, string> = {
  // The fallback only: a settled result says which of the two happened — see settledCopy.
  Settled: "The payment was completed.",
  AlreadySettled:
    "The payment was already completed, so this delivery changed nothing.",
  UnknownReference:
    "No payment carries this payment code. Nothing was changed.",
  NotATPayGateBill:
    "This payment was not opened as a T-PayGate QR bill, so the gateway route may not settle it.",
  PaymentClosed:
    "The payment was cancelled, rejected or refunded before the money arrived. Nothing was settled.",
  AmountMismatch:
    "The amount differs from the amount the bill was issued for. A bill settles only on the exact amount.",
  CurrencyNotSupported:
    "This payment is not in VND, the only currency a QR bill settles in. Nothing was settled.",
  OrderNotPayable:
    "The order behind this payment is no longer awaiting payment, or could not be found. Nothing was settled.",
  RenewalBlocked:
    "The VM this order renews is being deleted, being reinstalled, or gone. Nothing was settled.",
  FulfilmentNotApplied:
    "The renewal or upgrade behind this payment refused to apply, so the settlement was rolled back.",
};

/**
 * Why the simulator cannot run, or null when it can. The backend refuses a config that is switched
 * off or targets Production; this says so — and names the two states that look like those but are
 * not — before the operator fills the form.
 */
function unavailableReason(
  config: TPaygateConfigDto | undefined,
  loadFailed: boolean,
): string | null {
  // No answer is not "switched off": say that the state is unknown rather than guess one.
  if (loadFailed || !config) {
    return "The T-PayGate configuration could not be loaded, so the simulator cannot tell whether it may run. Reload the page.";
  }

  // Nothing saved yet: the API answers with an empty form whose switch starts on and whose
  // environment is blank, which would otherwise read as an active Production config.
  if (!config.environment) {
    return "T-PayGate has not been set up yet. Save its credentials in the T-PayGate settings before simulating a webhook.";
  }

  if (!config.isActive) {
    return "T-PayGate is switched off. Enable it in the T-PayGate settings before simulating a webhook.";
  }

  // Compared against the literal: an API older than this field leaves it undefined, and unknown
  // must not read as "Production".
  if (config.isSandbox === false) {
    return "The simulator runs only while T-PayGate targets the Staging environment. A Production bill is settled by the gateway's own callback, or by verifying the payment by hand.";
  }

  return null;
}

/** The local time a `datetime-local` field holds, as the ISO instant the API expects. */
function toIsoInstant(localDateTime: string): string | undefined {
  if (!localDateTime) {
    return undefined;
  }

  const parsed = new Date(localDateTime);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/** A `datetime-local` value for the instant a pasted callback names; blank when it has none. */
function toLocalDateTimeInput(value: unknown): string {
  if (typeof value !== "string" || value.trim() === "") {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const pad = (part: number) => String(part).padStart(2, "0");
  const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  return `${date}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/** A pasted text field as the form holds it. A number is accepted: a bill code may be all digits. */
function toTextInput(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }

  return typeof value === "number" ? String(value) : "";
}

/** Plain digits with at most two zero decimals: how a gateway prints a whole amount as text. */
const WHOLE_AMOUNT_TEXT = /^\d+(\.0{1,2})?$/;

/**
 * A pasted amount as the grouped text the field holds. Blank unless it is a whole number above
 * zero: a blank field fails its own "greater than zero" rule in front of the operator, where a
 * rounded or sign-stripped figure would be sent as if it were the one pasted — on the page that
 * exists to test what an amount differing from the bill does.
 */
function toPastedAmountInput(value: unknown): string {
  let amount = Number.NaN;
  if (typeof value === "number") {
    amount = value;
  }

  // Through Number(), not the keystroke mask: the mask reads "500000.00" as fifty million.
  // "500.000" is refused rather than read: it is as likely five hundred thousand with a
  // Vietnamese group separator as five hundred, and Number() would pick five hundred.
  if (typeof value === "string" && WHOLE_AMOUNT_TEXT.test(value.trim())) {
    amount = Number(value.trim());
  }

  if (!Number.isSafeInteger(amount) || amount <= 0) {
    return "";
  }

  return toAmountInput(amount);
}

/** The pasted object with its keys lower-cased, or null when the value is not an object. */
function lowerCaseKeys(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  const fields: Record<string, unknown> = {};
  for (const [key, fieldValue] of Object.entries(value)) {
    fields[key.toLowerCase()] = fieldValue;
  }

  return fields;
}

type PastedPayload =
  | { values: WebhookSimulationForm; error: null }
  | { values: null; error: string };

/**
 * Reads the callback fields out of pasted JSON: what "Copy payment details" on the client
 * console's QR screen copies, or a callback body as T-PayGate sends it — flat, or inside the v2
 * envelope's `Data`. Names match in any casing, because the gateway writes PascalCase and this
 * API camelCase. A field the text does not carry comes back blank.
 */
function parsePastedPayload(text: string): PastedPayload {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {
      values: null,
      error:
        "This is not valid JSON. Paste the details copied from the client console's QR screen, or a T-PayGate callback body.",
    };
  }

  const root = lowerCaseKeys(parsed);
  const fields = lowerCaseKeys(root?.data) ?? root;
  if (!fields) {
    return {
      values: null,
      error: "Paste a JSON object carrying the callback fields.",
    };
  }

  const values: WebhookSimulationForm = {
    refTransactionId: toTextInput(fields.reftransactionid),
    billCode: toTextInput(fields.billcode),
    amount: toPastedAmountInput(fields.amount),
    virtualAccount: toTextInput(fields.virtualaccount),
    actualAccount: toTextInput(fields.actualaccount),
    paymentTime: toLocalDateTimeInput(fields.paymenttime),
  };

  const foundAny = Object.values(values).some((value) => value !== "");
  if (!foundAny) {
    return {
      values: null,
      error:
        "None of the callback fields was found. Expected RefTransactionId, BillCode and Amount.",
    };
  }

  return { values, error: null };
}

/**
 * Stands in for T-PayGate's payment callback while the gateway's own webhook is not delivering:
 * the operator picks a QR bill a customer just opened, sends the callback by hand, and reads what
 * the settlement decided. It runs the real settlement; only the HTTP hop and its signature check
 * are skipped.
 */
export function TPaygateWebhookSimulatorPage() {
  const configQuery = useTPaygateConfig();
  const openBillsQuery = usePaymentsList({
    status: "AwaitingVerification",
    paymentMethod: "QRCode",
    pageSize: 20,
  });
  const simulate = useSimulateWebhook();
  const [result, setResult] = useState<WebhookSimulationResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState<string | null>(null);

  const form = useForm<WebhookSimulationForm>({
    resolver: zodResolver(webhookSimulationSchema),
    defaultValues: {
      refTransactionId: "",
      billCode: "",
      amount: "",
      virtualAccount: "",
      actualAccount: "",
      paymentTime: "",
    },
  });

  const blockedReason = configQuery.isLoading
    ? null
    : unavailableReason(configQuery.data, configQuery.isError);

  // The list filter cannot name the gateway, so bills of another QR provider are dropped here.
  const openBills = (openBillsQuery.data?.data ?? []).filter(
    (payment) => payment.qrProvider === "TPayGate",
  );

  const fillFromBill = (bill: PaymentListDto) => {
    form.reset({
      refTransactionId: bill.paymentOrderNumber,
      billCode: bill.tPayGateBillCode ?? "",
      amount: toAmountInput(bill.amount),
      virtualAccount: "",
      actualAccount: "",
      paymentTime: "",
    });
    setResult(null);
  };

  // The result renders under two tall cards, usually below the fold. Optional call because jsdom
  // has no scrollIntoView.
  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [result]);

  // Replaces the whole form, like picking a bill: a field the pasted text lacks must not keep a
  // value left over from the bill before it.
  const handleImport = () => {
    const pasted = parsePastedPayload(importText);
    if (pasted.values === null) {
      setImportError(pasted.error);
      return;
    }

    form.reset(pasted.values);
    setImportText("");
    setImportError(null);
    setResult(null);
  };

  const handleSubmit = async (values: WebhookSimulationForm) => {
    setResult(null);

    try {
      const simulation = await simulate.mutateAsync({
        refTransactionId: values.refTransactionId,
        billCode: values.billCode,
        amount: parseAmountInput(values.amount) ?? 0,
        virtualAccount: values.virtualAccount || undefined,
        actualAccount: values.actualAccount || undefined,
        paymentTime: toIsoInstant(values.paymentTime),
      });
      setResult(simulation);
      announceSimulation(simulation);
    } catch {
      // The mutation's own onError has already told the operator.
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="T-PayGate Webhook Simulator"
        subtitle="Send the payment callback T-PayGate would send for a QR bill, and see what the settlement decides"
        titleAdornment={<Badge variant="outline">Staging only</Badge>}
      />

      {blockedReason && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Simulator unavailable</AlertTitle>
          <AlertDescription>
            {blockedReason}{" "}
            <Link to={ROUTES.tpaygate} className="underline">
              Open T-PayGate settings
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>This settles the payment for real</AlertTitle>
        <AlertDescription>
          The simulated callback runs the same settlement as the gateway&apos;s
          own webhook — it completes the payment, credits a top-up to the
          wallet, and starts provisioning or renewal for an order. Only the HTTP
          request and its signature check are skipped. A bill settles only when
          the amount equals the amount it was issued for.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div className="space-y-1.5">
              <CardTitle>Open QR bills</CardTitle>
              <CardDescription>
                T-PayGate bills still awaiting their callback. Click one to fill
                the form with its values.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => openBillsQuery.refetch()}
              disabled={openBillsQuery.isFetching}
              title="Reload open bills"
              aria-label="Reload open bills"
            >
              <RefreshCw
                className={`h-4 w-4 ${openBillsQuery.isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </CardHeader>
          <CardContent>
            {openBillsQuery.isLoading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading open bills…
              </div>
            ) : openBillsQuery.error ? (
              <p className="py-8 text-sm text-destructive-ink">
                Could not load the open bills. Type the payment code by hand, or
                reload the list.
              </p>
            ) : openBills.length === 0 ? (
              <p className="py-8 text-sm text-muted-foreground">
                No T-PayGate bill is awaiting its callback. Create a QR payment
                in the client console, then reload this list.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment code</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>For</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {openBills.map((bill) => (
                    <TableRow
                      key={bill.paymentId}
                      className="cursor-pointer"
                      onClick={() => fillFromBill(bill)}
                    >
                      <TableCell className="font-mono text-xs">
                        {bill.paymentOrderNumber}
                      </TableCell>
                      <TableCell className="text-xs">
                        {bill.ownerEmail}
                      </TableCell>
                      <TableCell className="text-xs">
                        {orderTypeLabel(bill.orderType)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {formatCurrency(bill.amount, bill.currency)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatVietnamDateTime(bill.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Callback payload</CardTitle>
            <CardDescription>
              The six fields T-PayGate sends when a bill is paid. Change the
              amount to see a mismatch refused.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6 space-y-2 rounded-md border border-dashed p-3">
              <Label htmlFor="import-payment-details">
                Import payment details
              </Label>
              <Textarea
                id="import-payment-details"
                value={importText}
                onChange={(event) => {
                  setImportText(event.target.value);
                  setImportError(null);
                }}
                placeholder={
                  '{ "RefTransactionId": "WIC-PAY-…", "BillCode": "…", "Amount": 500000 }'
                }
                spellCheck={false}
                className="min-h-18 font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Paste what &quot;Copy payment details&quot; copied on the client
                console&apos;s QR screen, or a callback body from T-PayGate. It
                replaces every field below.
              </p>
              {importError && (
                <p role="alert" className="text-sm text-destructive-ink">
                  {importError}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleImport}
                disabled={importText.trim() === ""}
              >
                <ClipboardPaste className="mr-2 h-4 w-4" />
                Fill the form
              </Button>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                aria-busy={simulate.isPending}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="refTransactionId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment code (RefTransactionId)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="WIC-PAY-20261001083000-1234"
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="billCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bill code</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Bill code issued by T-PayGate"
                            autoComplete="off"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            inputMode="numeric"
                            placeholder="500,000"
                            autoComplete="off"
                            onChange={(event) =>
                              field.onChange(
                                formatAmountInput(event.target.value),
                              )
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          In đồng. Must equal the bill&apos;s amount to settle.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="virtualAccount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Virtual account</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Optional"
                            autoComplete="off"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="actualAccount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Actual account</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Optional"
                            autoComplete="off"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="paymentTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment time</FormLabel>
                      <FormControl>
                        <Input {...field} type="datetime-local" />
                      </FormControl>
                      <FormDescription>
                        Your local time. Leave blank to use now.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={simulate.isPending || blockedReason !== null}
                >
                  {simulate.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Send simulated webhook
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {result && (
        <div ref={resultRef}>
          <SimulationResult result={result} />
        </div>
      )}
    </div>
  );
}

/** Settled reads as success, a harmless repeat as neutral, every refusal or failure as an error. */
function outcomeBadgeVariant(
  result: WebhookSimulationResult,
): "default" | "secondary" | "destructive" {
  if (result.settled) {
    return "default";
  }

  return result.outcome === "AlreadySettled" ? "secondary" : "destructive";
}

/** A settled top-up moved the wallet; a settled order did not. The result says which it was. */
function settledCopy(result: WebhookSimulationResult): string {
  if (isTopUp(result.orderType)) {
    return "The payment was completed and the top-up was credited to the customer's wallet.";
  }

  return "The payment was completed. The order was marked paid and handed to fulfilment; the wallet did not move.";
}

/** What the settlement decided, in one sentence for the operator. */
function outcomeExplanation(result: WebhookSimulationResult): string {
  if (result.outcome === "Settled") {
    return settledCopy(result);
  }

  return (
    OUTCOME_COPY[result.outcome as TPaygateWebhookOutcome] ??
    result.message ??
    "The settlement failed. See the audit log for the cause."
  );
}

/**
 * Says at once what the delivery did; the result card carries the figures. Green only when the
 * bill really settled — the request succeeding is not the payment succeeding, and a refusal
 * announced in green is how a mismatched amount gets read as paid.
 */
function announceSimulation(result: WebhookSimulationResult) {
  const description = outcomeExplanation(result);

  if (result.settled) {
    toast.success("Webhook sent — the payment was settled", { description });
    return;
  }

  if (result.outcome === "AlreadySettled") {
    toast.info("Webhook sent — nothing changed", { description });
    return;
  }

  toast.warning("Webhook sent — the payment was not settled", { description });
}

/** What the settlement decided, and where it left the payment and its order. */
function SimulationResult({ result }: { result: WebhookSimulationResult }) {
  const explanation = outcomeExplanation(result);

  const currency = result.currency ?? "VND";
  const amountsDiffer =
    result.expectedAmount != null &&
    result.expectedAmount !== result.reportedAmount;

  return (
    <Card data-testid="simulation-result">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Result
          <Badge variant={outcomeBadgeVariant(result)}>{result.outcome}</Badge>
        </CardTitle>
        <CardDescription>{explanation}</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <ResultField label="Payment code">
            {result.paymentId ? (
              <Link
                to={ROUTES.payments.detail(result.paymentId)}
                className="font-mono underline"
              >
                {result.refTransactionId}
              </Link>
            ) : (
              <span className="font-mono">{result.refTransactionId}</span>
            )}
          </ResultField>
          <ResultField label="Payment status">
            {result.paymentStatus ?? "—"}
          </ResultField>
          <ResultField label="Bill amount">
            {result.expectedAmount != null
              ? formatCurrency(result.expectedAmount, currency)
              : "—"}
          </ResultField>
          <ResultField label="Reported amount">
            <span className={amountsDiffer ? "text-destructive-ink" : ""}>
              {formatCurrency(result.reportedAmount, currency)}
            </span>
          </ResultField>
          <ResultField label="Order">
            {result.orderNumber
              ? `${result.orderNumber} (${orderTypeLabel(result.orderType)})`
              : "—"}
          </ResultField>
          <ResultField label="Order status">
            {result.orderStatus ?? "—"}
          </ResultField>
        </dl>
      </CardContent>
    </Card>
  );
}

function ResultField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
